import { QueryParams } from '@/analytics/UserBrowser';
import { safeJsonParse } from '@asurion-core/utils';
import { ReadonlyURLSearchParams } from 'next/navigation';
import { isClient } from './isClient';

const BLOCKED_KEYS = [
  'customerSessionStartedEvent',
  'customerValidatedEvent',
  'state',
  'code',
  'error',
  'error_uri',
  'error_description',
];

let prevSearch: string | undefined = undefined;
let currentParams: Record<string, string> = {};
let prefixedParams: Record<string, string> = {};

const filterObjectKeys = <T>(
  obj: { [k: string]: T },
  keysToFilter: string[],
): { [k: string]: T } => {
  const clonedObject = { ...obj };

  const objectKeys = Object.keys(clonedObject);

  keysToFilter.forEach(
    (filterKey) =>
      delete clonedObject[
        objectKeys.find((objectKey: string) => {
          return objectKey.toLowerCase() === filterKey.toLowerCase();
        }) as string
      ],
  );

  return clonedObject;
};

export const getQueryParams = () => {
  if (!isClient()) {
    return {};
  }
  if (prevSearch !== window.location.search) {
    const urlSearchParams = new URLSearchParams(window?.location.search);

    currentParams = Object.fromEntries(urlSearchParams.entries());
    prefixedParams = {};

    for (const key in currentParams) {
      prefixedParams[`QP${key}`] = currentParams[key];
    }

    prevSearch = window.location.search;
  }

  return prefixedParams;
};

export const getUrlData = () => {
  const queryParams = getQueryParams();
  const filteredParams = filterObjectKeys(queryParams, BLOCKED_KEYS);

  return {
    URL: isClient() ? window.location.origin : null,
    Route: isClient()
      ? window.location.pathname.replace(/\//, '').replace(/\/+$/, '')
      : null,
    ...filteredParams,
  };
};

/**
 * @param searchParams - The query string to add/update the specified params to
 * @param key - The key of the param to add/update
 * @param value - The value of the param to add/update
 * @returns The resulting query string after removing params
 */
export const appendQueryParam = (
  searchParams: ReadonlyURLSearchParams,
  key: string,
  value: string,
) => {
  const params = new URLSearchParams(searchParams);
  params.set(key, value);
  return params.toString();
};

/**
 * @param searchParams - The query string to remove params from
 * @param paramsToRemove - An array of query params (strings) to remove from the
 *   query string
 * @returns The resulting query string after removing params
 */
export const removeQueryParams = (
  searchParams: ReadonlyURLSearchParams,
  paramsToRemove: string[],
) => {
  const params = new URLSearchParams(searchParams);
  paramsToRemove.forEach((param) => {
    params.delete(param);
  });
  return params.toString();
};

export type HeaderType = { key: string; value: string };

export type QueryParamAdjustments = {
  toRemove: string[];
  toAdd: HeaderType[];
};

/**
 * @param searchParams - The query string to adjust
 * @param queryParamAdjustments - An array of query params (strings) to remove
 *   from the query string
 * @returns The resulting query string after adding and removing params
 */
export const removeAndAppendQueryParams = (
  searchParams: ReadonlyURLSearchParams,
  queryParamAdjustments: QueryParamAdjustments,
) => {
  const params = new URLSearchParams(searchParams);
  queryParamAdjustments.toRemove.forEach((param) => {
    params.delete(param);
  });
  queryParamAdjustments.toAdd.forEach((param) => {
    params.set(param.key, param.value);
  });
  return params.toString();
};

/**
 * @param params - The query string to parse marketing params from
 * @returns An object containing the parsed marketing query params
 */
export function parseMarketingParamsFromURL(params: URLSearchParams) {
  return {
    salesPersonId: params.get(QueryParams.AgentId),
    salesSessionId: params.get(QueryParams.SalesSessionId),
    storeCode: params.get(QueryParams.StoreCode),
    dealerCode: params.get(QueryParams.DealerCode),
    referralPartner: params.get(QueryParams.ReferrerCode) || 'Asurion',
    vendorData: {
      v_data: safeJsonParse(params.get(QueryParams.VendorData) || '{}'),
      ...Object.fromEntries(params),
    },
    salesChannel:
      params.get(QueryParams.SalesChannel) || 'Digital_registration_web',
    utmParams: {
      utmCampaign: params.get(QueryParams.UtmCampaign),
      utmContent: params.get(QueryParams.UtmContent),
      utmMedium: params.get(QueryParams.UtmMedium),
      utmSource: params.get(QueryParams.UtmSource),
    },
  };
}
